import React, { useState, useCallback, useMemo } from 'react';
import { LoadingSpinner, EmptyState } from '../../components/layout/SharedComponents';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import InteractiveTable from '../../components/InteractiveTable';
import { sortData } from './sortUtils';

const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const defaultCols = [
  { key: 'item_code', label: 'Item Code', width: 140, sortField: 'item_code', visible: true },
  { key: 'description', label: 'Description', width: 260, sortField: 'description', visible: true },
  { key: 'location_count', label: 'Locations', width: 100, sortField: 'location_count', align: 'right', visible: true },
  { key: 'total_quantity', label: 'Total Qty', width: 120, sortField: 'total_quantity', align: 'right', visible: true },
  { key: 'below_minimum', label: 'Status', width: 110, sortField: 'below_minimum', visible: true },
];

export default function StockItemsList({ items, loading, onItemClick }) {
  const [sortState, setSortState] = useState({ field: 'item_code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);

  const sorted = useMemo(() => sortData(items, sortState.field, sortState.dir), [items, sortState]);

  const renderCell = useCallback((col, item) => {
    switch (col.key) {
      case 'item_code': return <span className="text-zinc-900 font-semibold text-sm">{item.item_code}</span>;
      case 'description': return <span className="text-sm">{item.description}</span>;
      case 'location_count': return <span className="text-sm">{item.location_count}</span>;
      case 'total_quantity': return <span className="text-sm tabular-nums">{fmtNum(item.total_quantity)}</span>;
      case 'below_minimum': return item.below_minimum
        ? <Badge className="text-[9px] bg-red-100 text-red-700"><AlertTriangle className="h-2.5 w-2.5 inline mr-0.5" /> Below Min</Badge>
        : <Badge className="text-[9px] bg-green-100 text-green-700">OK</Badge>;
      default: return <span className="text-sm">{item[col.key] != null ? String(item[col.key]) : '—'}</span>;
    }
  }, []);

  if (loading) return <LoadingSpinner />;
  if (items.length === 0) {
    return <EmptyState title="No inventoried items" description="Flag items as 'Stock Item' in the Items database to track inventory." />;
  }

  return (
    <InteractiveTable
      tableId="stock-items-listing"
      defaultCols={defaultCols}
      data={sorted}
      renderCell={renderCell}
      onRowClick={(row) => onItemClick(row.item_code)}
      sortState={sortState}
      onSort={handleSort}
      rowKey="item_code"
    />
  );
}
